import { create } from 'zustand';
import type { User } from '../types';
import { apiService } from '../services/api';
import { logger } from '../utils/logger';


interface AppState {
  isLoading: boolean;
  error: string | null;
  user: User | null;
  isAuthenticated: boolean;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  setUser: (user: User | null) => void;
  updateUser: (userData: Partial<User>) => void;
  login: (username: string, password: string, schoolCode?: string | null) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

// Initialize user from sessionStorage on store creation
const initializeUser = (): User | null => {
  try {
    const storedUser = sessionStorage.getItem('user');
    if (storedUser) {
      return JSON.parse(storedUser);
    }
  } catch (error) {
    logger.error('Error initializing user from sessionStorage:', error);
    sessionStorage.removeItem('user');
  }
  return null;
};

const initialUser = initializeUser();

export const useAppStore = create<AppState>((set, get) => ({
  isLoading: false,
  error: null,
  user: initialUser,
  isAuthenticated: !!initialUser,
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),
  setUser: (user) => {
    if (user) {
      sessionStorage.setItem('user', JSON.stringify(user));
    } else {
      sessionStorage.removeItem('user');
    }
    set({ 
      user, 
      isAuthenticated: !!user 
    });
  },
  updateUser: (userData) => {
    const currentUser = get().user;
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData };
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
      set({ user: updatedUser });
    }
  },
  login: async (username: string, password: string, schoolCode?: string | null) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await apiService.login(username, password, schoolCode);
      
      if (response.success && response.user) {
        get().setUser(response.user);
        return true;
      } else {
        set({ error: response.message || 'Đăng nhập thất bại' });
        return false;
      }
    } catch (error: any) {
      // Extract error message from API response
      // apiService throws Error with message from backend
      const errorMessage = error?.message || 'Lỗi kết nối đến server';
      
      // Check if it's a network/connection error
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        set({ error: 'Lỗi kết nối đến server' });
      } else {
        // Use the error message from backend (e.g., "Tài khoản không hợp lệ")
        set({ error: errorMessage });
      }
      return false;
    } finally {
      set({ isLoading: false });
    }
  },
  logout: async () => {
    try {
      // Try to call logout endpoint, but don't fail if it errors (404, etc.)
      // The logout is primarily client-side anyway
      await apiService.logout();
    } catch (error: any) {
      // Silently handle logout errors - logout is primarily client-side
      // 404 errors are expected if endpoint doesn't exist yet on server
      if (error?.message && !error.message.includes('404')) {
        logger.warn('Logout API call failed:', error);
      }
    } finally {
      // Always clear user data client-side regardless of API call result
      get().setUser(null);
    }
  },
  checkAuth: async () => {
    try {
      const response = await apiService.getCurrentUser();
      if (response.success && response.user) {
        get().setUser(response.user);
      } else {
        get().setUser(null);
      }
    } catch (error) {
      get().setUser(null);
    }
  },
}));
